import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useWaitlist } from '../context/WaitlistContext';
import ThinkDreamSection from '../components/ThinkDreamSection';
import CTASection from '../components/CTASection';
import '../styles/Home.css';
import CharactersLocationsSection from '../components/CharactersLocationsSection';
import UIScript from '../components/UIScript';
import UIIdeas from '../components/UIIdeas';
import UIBeats from '../components/UIBeats';
import UICharacters from '../components/UICharacters';
import UILocations from '../components/UILocations';

const Home = () => {
  const [productImage, setProductImage] = useState('product-1.png');
  const [storyMapDots, setStoryMapDots] = useState([]);
  const storyMapLineRef = useRef(null);
  const purpleIndicatorRef = useRef(null);
  const [activeTab, setActiveTab] = useState('script');
  const [shouldAnimate, setShouldAnimate] = useState(true);
  const location = useLocation();
  const { openModal } = useWaitlist();

  useEffect(() => {
    // Trigger animation when navigating to Home page
    setShouldAnimate(true);

    // Disable animation after it completes (0.6s delay + 0.8s duration = 1.4s)
    const timer = setTimeout(() => {
      setShouldAnimate(false);
    }, 1400);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    const updateProductImage = () => {
      if (window.innerWidth < 768) {
        setProductImage('product-2.png');
      } else {
        setProductImage('product-1.png');
      }
    };

    updateProductImage();
    window.addEventListener('resize', updateProductImage);

    return () => window.removeEventListener('resize', updateProductImage);
  }, []);

  useEffect(() => {
    const lineElement = storyMapLineRef.current;
    if (!lineElement) {
      return;
    }

    let frameId;
    let resizeTimeout;

    const computeDots = () => {
      const width = lineElement.offsetWidth;
      if (!width) {
        frameId = requestAnimationFrame(computeDots);
        return;
      }

      const isMobile = window.innerWidth < 480;
      const count = isMobile ? 20 : 32; // Fewer dots on mobile for better spacing

      let seed = 1037;
      const random = () => {
        seed = (seed * 1664525 + 1013904223) % 4294967296;
        return seed / 4294967296;
      };

      const positions = [0];

      if (isMobile) {
        // On mobile: use random positions but ensure distribution across full width
        // Add padding from edges to prevent truncation (6px on each side for dot radius)
        const padding = 6;
        const usableWidth = width - (padding * 2);

        // Generate random positions with full randomness
        for (let i = 1; i < count - 1; i += 1) {
          positions.push(padding + random() * usableWidth);
        }

        // Always place last dot near the end (but not exactly at end for natural look)
        positions.push(padding + usableWidth * (0.92 + random() * 0.08));
      } else {
        // Desktop: original behavior
        for (let i = 1; i < count; i += 1) {
          positions.push(random() * width);
        }
      }

      positions.sort((a, b) => a - b);
      positions[0] = 0; // First dot at start

      // On desktop, ensure last dot reaches the end
      if (!isMobile) {
        positions[positions.length - 1] = width;
      }

      setStoryMapDots(
        positions.map((left, index) => ({
          id: index,
          left,
        }))
      );
    };

    computeDots();

    // Recompute on resize with debounce
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        computeDots();
      }, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (purpleIndicatorRef.current) {
        const indicator = purpleIndicatorRef.current;
        const section = indicator.closest('.writing-section');

        if (!section) return;

        const sectionTop = section.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        const sectionHeight = section.offsetHeight;

        // Only start parallax when section enters viewport
        if (sectionTop < windowHeight && sectionTop > -sectionHeight) {
          // Calculate scroll progress within the section
          const scrollProgress = Math.max(0, (windowHeight - sectionTop) / (windowHeight + sectionHeight));
          const parallaxSpeed = 200; // Maximum pixels to move
          const translateX = scrollProgress * parallaxSpeed;
          indicator.style.transform = `translateX(${translateX}px)`;
        } else if (sectionTop >= windowHeight) {
          // Reset position when section is above viewport
          indicator.style.transform = `translateX(0px)`;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="home">
      <section
        className="hero lp-hero"
      >
        <div className="container">
          <h1 className="hero-title">Powering the next wave of screenwriters</h1>
          <p className="hero-subtitle">
            Meet your creative partner for ideation, outlining, and writing your next masterpiece.
          </p>
          <div className="hero-buttons">
            <a href="https://app.fiktion.io/signup" target="_blank" rel="noopener noreferrer"><button className="btn btn-large btn-primary">Start writing</button></a>
          </div>
        </div>
        <div className="paper paper-1"><div className="flex space-between flex-column"><div className="spine-circle"></div><div className="spine-circle"></div><div className="spine-circle"></div><div className="spine-circle"></div></div></div>
        <div className="paper paper-2"><div className="flex space-between flex-column"><div className="spine-circle"></div><div className="spine-circle"></div><div className="spine-circle"></div><div className="spine-circle"></div></div></div>
        <div className="paper paper-3"><div className="flex space-between flex-column"><div className="spine-circle"></div><div className="spine-circle"></div><div className="spine-circle"></div><div className="spine-circle"></div></div></div>
      </section>
      {activeTab === 'script' ? (
        <UIScript
          selectedTab={activeTab}
          onTabChange={setActiveTab}
          pageClassName={`ui-script-home ${shouldAnimate ? 'ui-animate-in' : ''}`}
          enableClicking={true}
        />
      ) : activeTab === 'beats' ? (
        <UIBeats selectedTab={activeTab} onTabChange={setActiveTab} addClickableWrapper={true} pageClassName={`ui-beats-home ${shouldAnimate ? 'ui-animate-in' : ''}`} enableClicking={true} />
      ) : activeTab === 'characters' ? (
        <UICharacters selectedTab={activeTab} onTabChange={setActiveTab} addClickableWrapper={true} pageClassName={`ui-characters-home ${shouldAnimate ? 'ui-animate-in' : ''}`} enableClicking={true} />
      ) : activeTab === 'locations' ? (
        <UILocations selectedTab={activeTab} onTabChange={setActiveTab} addClickableWrapper={true} pageClassName={`ui-locations-home ${shouldAnimate ? 'ui-animate-in' : ''}`} enableClicking={true} />
      ) : (
        <UIIdeas selectedTab={activeTab} onTabChange={setActiveTab} addClickableWrapper={true} pageClassName={`ui-ideas-home ${shouldAnimate ? 'ui-animate-in' : ''}`} enableClicking={true} />
      )}
      <section className="features">
        <div className="container">
          <h2 className="features-title">Made for every big dreaming storyteller</h2>
          <div className="features-grid">
            <div className="feature">
              <h3>Ideate</h3>
              <p>Jot down ideas, chirps of dialogue and sudden sparks of genius.</p>
              <div className="feature-img-wrapper">
                <div className="fake-ui sidebar">
                  <div className="fake-ui-sidebar-item">
                    <div className="flex space-between w-100">
                      <div className="flex gap-8 flex-row items-center">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9.97239 1.26001C8.05451 1.26001 6.49976 2.81476 6.49976 4.73265C6.49976 5.28069 6.62671 5.79908 6.85281 6.26001H13.092C13.3181 5.79908 13.445 5.28069 13.445 4.73265C13.445 2.81476 11.8903 1.26001 9.97239 1.26001Z" fill="#272729" />
                          <path d="M6.99976 7.76001H12.9998" stroke="#272729" stroke-width="1.25" stroke-linecap="round" />
                          <path d="M5.66687 14.0856C7.50902 12.0804 7.66166 9.05521 7.49976 7.76001H12.4998C12.3379 9.05521 12.4905 12.0804 14.3326 14.0856C14.3929 14.1512 14.3474 14.26 14.2583 14.26H5.74118C5.65209 14.26 5.6066 14.1512 5.66687 14.0856Z" fill="#272729" />
                          <path d="M3.86578 17.2241L4.71108 15.76C4.88971 15.4506 5.21984 15.26 5.57711 15.26H14.4224C14.7797 15.26 15.1098 15.4506 15.2884 15.76L16.1337 17.2241C16.5186 17.8908 16.0375 18.7241 15.2677 18.7241H4.7318C3.962 18.7241 3.48088 17.8908 3.86578 17.2241Z" fill="#272729" />
                        </svg>
                        <p>The Fall of Order</p>
                      </div>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g opacity="0.5">
                          <path d="M10 8.11695L12.6443 8.11695C13.3749 8.11695 13.7519 8.99031 13.2506 9.52194L10.6063 12.3266C10.2774 12.6755 9.7226 12.6755 9.39366 12.3266L6.74937 9.52194C6.24814 8.99031 6.62505 8.11695 7.3557 8.11695L10 8.11695Z" fill="#040404" />
                        </g>
                      </svg>
                    </div>
                  </div>
                  <div className="fake-ui-sidebar-item indented">
                    <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3.75" y="4.82166" width="5" height="5.5" rx="1" stroke="#040404" stroke-width="1.25" />
                      <rect x="11.25" y="4.82166" width="5" height="4" rx="1" stroke="#040404" stroke-width="1.25" />
                      <rect x="3.75" y="12.8217" width="5" height="4" rx="1" stroke="#040404" stroke-width="1.25" />
                      <rect x="11.25" y="11.3217" width="5" height="5.5" rx="1" stroke="#040404" stroke-width="1.25" />
                    </svg>
                    <p>Beats</p>
                  </div>

                  <div className="fake-ui-sidebar-item indented">
                    <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.65552 8.07093C4.65552 7.32596 4.65552 6.95347 4.71713 6.64372C4.97015 5.37172 5.96449 4.37737 7.2365 4.12436C7.54625 4.06274 7.91873 4.06274 8.6637 4.06274C9.90531 4.06274 10.5261 4.06274 11.0424 4.16543C13.1624 4.58713 14.8196 6.24437 15.2413 8.36438C15.344 8.88063 15.344 9.50143 15.344 10.743V12.0791C15.344 13.5732 15.344 14.3202 15.0999 14.9095C14.7745 15.6952 14.1502 16.3194 13.3645 16.6448C12.7753 16.8889 12.0282 16.8889 10.5342 16.8889H9.81806C7.99196 16.8889 7.07891 16.8889 6.38393 16.5279C5.79827 16.2237 5.32075 15.7462 5.01653 15.1605C4.65552 14.4655 4.65552 13.5525 4.65552 11.7264V8.07093Z" stroke="#040404" stroke-width="1.24597" />
                      <path d="M9.99976 4.59717C9.99976 5.20503 9.99976 5.71973 9.99976 6.20058C9.99976 7.97151 11.4354 9.40699 13.2063 9.40699H14.8096" stroke="#040404" stroke-width="1.24597" stroke-linecap="round" />
                    </svg>
                    <p>Script</p>
                  </div>
                  <div className="fake-ui-sidebar-item indented">
                    <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3.5" y="3.62952" width="13" height="13" rx="3" stroke="#040404" stroke-width="1.25" />
                      <circle cx="10" cy="8.62952" r="2.25" fill="#040404" />
                      <path d="M7.13479 14.192H10.0002H12.8655C13.3771 14.192 13.7918 13.7773 13.7918 13.2657C13.7918 12.8521 13.5177 12.4886 13.12 12.375L12.3898 12.1664C10.828 11.7202 9.17235 11.7202 7.61047 12.1664L6.88032 12.375C6.48266 12.4886 6.2085 12.8521 6.2085 13.2657C6.2085 13.7773 6.62321 14.192 7.13479 14.192Z" fill="#040404" />
                    </svg>
                    <p>Characters</p>
                  </div>
                  <div className="fake-ui-sidebar-item indented">
                    <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11.3997 7.98364H8.59975C6.86235 7.98364 6.49976 8.34624 6.49976 10.0836V17.7836H13.4997V10.0836C13.4997 8.34624 13.1371 7.98364 11.3997 7.98364Z" stroke="#040404" stroke-width="1.25" stroke-linejoin="round" />
                      <path d="M9.30005 10.7837H10.7M9.30005 12.8837H10.7M9.30005 14.9837H10.7" stroke="#040404" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M16.5 17.7837V8.11369C16.5 7.25389 16.5 6.82399 16.2909 6.47851C16.0818 6.13303 15.7053 5.94078 14.9522 5.55626L11.7127 4.00444C10.9005 3.58968 10.6997 3.73586 10.6997 4.6438V7.77612" stroke="#040404" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M3.5 17.7837V11.4837C3.5 10.9046 3.62087 10.7837 4.2 10.7837H6.49994" stroke="#040404" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M17.5 17.7837H2.5" stroke="#040404" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <p>Locations</p>
                  </div>
                  <div className="fake-ui-sidebar-item indented selected">
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.0981 4.13696V17.4703" stroke="#272729" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M3.43701 10.809L16.7703 10.809" stroke="#272729" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M5.39331 15.5215L14.8214 6.09339" stroke="#272729" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M14.8215 15.5139L5.39344 6.08583" stroke="#272729" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <p>Ideas</p>
                  </div>
                </div>
                <img src={`${process.env.PUBLIC_URL}/images/ideate.png`} alt="Brainstorm" />
              </div>
            </div>
            <div className="feature">
              <h3>Outline your story</h3>
              <p>Create scenes and sequences to structure your story.</p>
              <div className="feature-img-wrapper">
                <div className="fake-ui toolbox">
                  <div className="fake-toolbox-popout">
                    <div className="fake-ui-sidebar-item">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clip-path="url(#clip0_445_6056)">
                          <rect x="0.625" y="0.625" width="14.75" height="14.75" rx="2.80357" stroke="#272729" stroke-width="1.25" />
                          <path d="M4 4.57153H10.8571M4 7.42868H8" stroke="#272729" stroke-width="1.25" stroke-linecap="round" />
                        </g>
                      </svg>
                      <p>Create scene</p>
                    </div>
                    <div className="fake-ui-sidebar-item">
                      <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.5191 5.6507V6.96058H10.9399V5.6507H15.5191ZM12.168 3.64221H13.7998V11.5725C13.7998 11.889 13.8472 12.1273 13.9418 12.2874C14.0364 12.4439 14.1582 12.5512 14.3074 12.6094C14.4602 12.664 14.6258 12.6913 14.8041 12.6913C14.9351 12.6913 15.0497 12.6822 15.1479 12.664C15.2462 12.6458 15.3226 12.6313 15.3772 12.6204L15.6719 13.9684C15.5773 14.0048 15.4427 14.0412 15.268 14.0776C15.0934 14.1176 14.875 14.1395 14.6131 14.1431C14.1837 14.1504 13.7835 14.074 13.4123 13.9139C13.0412 13.7538 12.741 13.5063 12.5118 13.1716C12.2826 12.8369 12.168 12.4166 12.168 11.9108V3.64221Z" fill="#272729" />
                        <path d="M1.32812 4.30823V2.85645H9.9788V4.30823H6.49124V14.0341H4.81023V4.30823H1.32812Z" fill="#272729" />
                      </svg>
                      <p>Create sequence</p>
                    </div>
                  </div>
                  <div className="fake-toolbox">
                    <div class="flex gap-2 items-center">
                      <div className="fake-toolbox-icon first-icon">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g clip-path="url(#clip0_445_6056)">
                            <rect x="0.625" y="0.625" width="14.75" height="14.75" rx="2.80357" stroke="#272729" stroke-width="1.25" />
                            <path d="M4 4.57153H10.8571M4 7.42868H8" stroke="#272729" stroke-width="1.25" stroke-linecap="round" />
                          </g>
                        </svg>
                      </div>
                      <div className="selector">
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1.11987 2.91992L3.99987 5.79992L6.87987 2.91992" stroke="#272729" stroke-width="1.08" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                      </div>
                    </div>
                    <div className="icon-divider"></div>
                    <div class="flex">
                      <div className="fake-toolbox-icon">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="0.625" y="0.625" width="14.75" height="14.75" rx="3.375" stroke="#272729" stroke-width="1.25" />
                          <rect x="6.33936" y="6.33936" width="9.03571" height="9.03571" rx="3.375" stroke="#272729" stroke-width="1.25" />
                        </svg>
                      </div>
                      <div className="fake-toolbox-icon">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="0.625" y="0.625" width="14.75" height="5.60714" rx="1.375" stroke="#272729" stroke-width="1.25" />
                          <rect x="0.625" y="9.76782" width="14.75" height="5.60714" rx="1.375" stroke="#272729" stroke-width="1.25" />
                        </svg>
                      </div>
                      <div className="fake-toolbox-icon">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="0.604004" y="1" width="14.7925" height="1.32075" rx="0.660377" fill="#272729" />
                          <rect x="0.604004" y="5.22632" width="14.7925" height="1.32075" rx="0.660377" fill="#272729" />
                          <rect x="0.604004" y="9.45288" width="14.7925" height="1.32075" rx="0.660377" fill="#272729" />
                          <rect x="0.604004" y="13.6792" width="14.7925" height="1.32075" rx="0.660377" fill="#272729" />
                        </svg>
                      </div>
                    </div>
                    <div className="icon-divider"></div>
                    <div className="flex">
                      <div className="fake-toolbox-icon">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g clip-path="url(#clip0_2285_3308)">
                            <path d="M4.74512 3.93237V12.068" stroke="#272729" stroke-width="1.255" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M8 5.01685V10.9829" stroke="#272729" stroke-width="1.255" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M11.2549 6.10156V9.89817" stroke="#272729" stroke-width="1.255" stroke-linecap="round" stroke-linejoin="round" />
                            <rect x="0.625" y="0.625" width="14.75" height="14.75" rx="3.375" stroke="#272729" stroke-width="1.25" />
                          </g>
                        </svg>
                      </div>
                    </div>
                    <div className="icon-divider"></div>
                    <div className="fake-switch">
                      <div className="fake-switch-toggle">
                        <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="2.47266" y="8.13818" width="8.94706" height="2.75294" rx="0.688235" stroke="#272729" stroke-width="0.75" />
                          <rect x="0.407959" y="3.32056" width="9.63529" height="2.75294" rx="0.688235" stroke="#272729" stroke-width="0.75" />
                          <path d="M6.08594 1.25598V12.956" stroke="#272729" stroke-width="0.75" stroke-linecap="round" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <img src={`${process.env.PUBLIC_URL}/images/outline.jpg`} alt="Outline" />
                </div>
              </div>
            </div>
            <div className="feature">
              <h3>Write your screenplay</h3>
              <p>Get in flow with industry standard formatting and intuitive features.</p>
              <div className="feature-img-wrapper">
                <div className="fake-ui writing">
                  <div className="fake-ui-sidebar-item">Scene Heading</div>
                  <div className="fake-ui-sidebar-item selected">Action</div>
                  <div className="fake-ui-sidebar-item">Character</div>
                  <div className="fake-ui-sidebar-item">Parenthetical</div>
                  <div className="fake-ui-sidebar-item">Dialogue</div>
                </div>
                <div className="fake-gradient"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider"></div>

      <section className="section-marg writing-section">
        <div className="container">
          <label>Write</label>
          <h2 className="section-title">Bring clarity to every page </h2>
          <p className="section-subtitle">From the smallest tweak to the biggest rewrite; Fiktion gives you an intiutive and professional writing experience, with industry standard formatting, timelines, shortcuts, versioning and much more. </p>

          <div className="script-wrapper">
            <div className="fake-story-map">
              <div className="fake-story-map-line" ref={storyMapLineRef}>
                {storyMapDots.map((dot) => (
                  <span
                    key={dot.id}
                    className="story-map-dot"
                    style={{ left: `${dot.left}px` }}
                  />
                ))}
              </div>
              <div className="story-map-indicators">
                <div className="story-map-number">1</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">5</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">10</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">15</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">20</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">25</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">30</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">35</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">40</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">45</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">50</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
                <div className="story-map-number">55</div>
                <div className="bars-wrapper"><div className="bars"></div><div className="bars"></div><div className="bars"></div><div className="bars"></div>
                </div>
              </div>
              <div className="purple-indicator" ref={purpleIndicatorRef}></div>
            </div>
            <div className="fake-script-wrapper">
              <div className="fake-script-content">
                <div className="script-character">CARA</div>
                <div className="script-dialogue">You were with a client or were you the client?</div>
                <div className="script-action">Jack folds a napkin in his lap and claps back:</div>
                <div className="script-character">JACK</div>
                <div className="script-dialogue">Good one. Hey -- how’s your marriage?</div>
                <div className="script-character">CARA</div>
                <div className="script-parenthetical">(fakes a smile)</div>
                <div className="script-dialogue">Great. Never been better.</div>
                <div className="fake-ui writing">
                  <div className="fake-ui-sidebar-item">Scene Heading</div>
                  <div className="fake-ui-sidebar-item selected">Action</div>
                  <div className="fake-ui-sidebar-item">Character</div>
                  <div className="fake-ui-sidebar-item">Parenthetical</div>
                  <div className="fake-ui-sidebar-item">Dialogue</div>
                  <div className="fake-ui-sidebar-item">Transition</div>
                  <div className="fake-ui-sidebar-item">Shot</div>
                </div>
              </div>
            </div>
            <div
              className="section-image-large writing-image"
              role="img"
              aria-label="Writing"
              style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/write.jpg)` }}
            ></div>
          </div>

          <div className="section-features">
            <div className="section-feature">
              <div className="section-title-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1.5" y="5.90674" width="15" height="6.17647" rx="1.5" stroke="#040404" stroke-width="1.25" />
                  <path d="M5.0294 3.75849V14.2316M5.0294 3.75849L3.70587 3.25977M5.0294 3.75849L6.35293 3.25977M5.0294 14.2316L3.70587 14.7304M5.0294 14.2316L6.35293 14.7304" stroke="#040404" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <p>Story map</p>
              </div>
              <div className="section-feature-text">
                <p>Bring out the story map, whenever you need to move swiftly between scenes.</p>
              </div>
            </div>
            <div className="section-feature">
              <div className="section-title-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_1071_23592)">
                    <rect x="4.28349" y="10.4119" width="12.2353" height="3.76471" rx="1" stroke="#040404" stroke-width="1.25" />
                    <rect x="1.45996" y="3.82349" width="13.1765" height="3.76471" rx="1" stroke="#040404" stroke-width="1.25" />
                    <path d="M9.22467 1V17" stroke="#040404" stroke-width="1.25" stroke-linecap="round" />
                  </g>
                </svg>
                <p>Beat timeline</p>
              </div>
              <div className="section-feature-text">
                <p>Summon the beat timeline as you write, to make sure your story matches your structure.</p>
              </div>
            </div>
            <div className="section-feature">
              <div className="section-title-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.9043 13.1626C12.7603 14.4407 11.0979 15.2451 9.24756 15.2451C5.79713 15.2451 3 12.448 3 8.99756C3 5.54713 5.79713 2.75 9.24756 2.75C11.56 2.75 13.5791 4.00638 14.6593 5.87378M14.6593 5.87378L15 3.15847M14.6593 5.87378H11.8894" stroke="#040404" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M8.72778 6.39404V9.51782L10.8103 11.6003" stroke="#040404" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <p>Versioning</p>
              </div>
              <div className="section-feature-text">
                <p>Go back and restore older versions, or to copy that perfect piece of dialogue you discarded.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider"></div>

      <section className="section-marg outline-section">
        <label>Outline</label>
        <h2 className="section-title">Define the direction<br></br>of your story</h2>
        <p className="section-subtitle">Move your story from start to finish with an intuitive canvas, cards and timeline, that works the way you do. </p>
        <div className="fake-ui canvas">
          <div className="fake-timeline ui-timeline">
            <div className="fake-timeline-list">
              <div className="fake-timeline-list-item bordered">Acts</div>
              <div className="fake-timeline-list-item bordered">Sequences</div>
              <div className="fake-timeline-list-item">Scenes</div>
            </div>
            <div className="fake-timeline-content">
              <div className="fake-timeline-column">
                <div className="fake-timeline-content-item-title blue">Act 1</div>
                <div className="fake-timeline-content-sequence-title">The routine</div>
                <div className="fake-timeline-scenes">
                  <div className="fake-timeline-content-beat-title">Morning machine</div>
                  <div className="fake-timeline-content-beat-title">A glitch in the system</div>
                  <div className="fake-timeline-content-beat-title">Applause on cue</div>
                </div>
              </div>
              <div className="fake-timeline-column">
                <div className="fake-timeline-content-item-title mint">Act 2</div>
                <div className="fake-timeline-content-sequence-title">A deal in disguise</div>
              </div>
              <div className="fake-timeline-column">
                <div className="fake-timeline-content-item-title purple">Act 3</div>
              </div>
              <div className="fake-timeline-column">
                <div className="fake-timeline-content-item-title unassigned">Unassigned</div>
              </div>
            </div>
            <div className="fake-ui-empty-timeline"></div>
          </div>
          <div className="fake-beat one">
            <div className="flex flex-col space-between">
              <div class="flex flex-column gap-8">
                <p className="fake-beat-title">Morning machine</p>
                <div className="hero-beat-placeholder"></div>
                <div className="hero-beat-placeholder w-60"></div>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="act-chip">Act 1</div>
              <div className="sequence-chip">The routine</div>
            </div>
          </div>
          <div className="fake-beat two">
            <div className="flex flex-col space-between">
              <div class="flex flex-column gap-8">
                <p className="fake-beat-title">A glitch in the system</p>
                <div className="hero-beat-placeholder w-90"></div>
                <div className="hero-beat-placeholder w-80"></div>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="act-chip">Act 1</div>
              <div className="sequence-chip">The routine</div>
            </div>
          </div>
          <div className="fake-beat three">
            <div className="flex flex-col space-between">
              <div class="flex flex-column gap-8">
                <p className="fake-beat-title">Applause on cue</p>
                <div className="hero-beat-placeholder"></div>
                <div className="hero-beat-placeholder w-70"></div>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="act-chip">Act 1</div>
              <div className="sequence-chip">The routine</div>
            </div>
          </div>
          <div className="fake-toolbox-wrapper">
            <div className="fake-toolbox absolute">
              <div class="flex gap-2 items-center">
                <div className="fake-toolbox-icon first-icon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_445_6056)">
                      <rect x="0.625" y="0.625" width="14.75" height="14.75" rx="2.80357" stroke="#272729" stroke-width="1.25" />
                      <path d="M4 4.57153H10.8571M4 7.42868H8" stroke="#272729" stroke-width="1.25" stroke-linecap="round" />
                    </g>
                  </svg>
                </div>
                <div className="selector">
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1.11987 2.91992L3.99987 5.79992L6.87987 2.91992" stroke="#272729" stroke-width="1.08" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
              </div>
              <div className="icon-divider"></div>
              <div class="flex">
                <div className="fake-toolbox-icon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="0.625" y="0.625" width="14.75" height="14.75" rx="3.375" stroke="#272729" stroke-width="1.25" />
                    <rect x="6.33936" y="6.33936" width="9.03571" height="9.03571" rx="3.375" stroke="#272729" stroke-width="1.25" />
                  </svg>
                </div>
                <div className="fake-toolbox-icon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="0.625" y="0.625" width="14.75" height="5.60714" rx="1.375" stroke="#272729" stroke-width="1.25" />
                    <rect x="0.625" y="9.76782" width="14.75" height="5.60714" rx="1.375" stroke="#272729" stroke-width="1.25" />
                  </svg>
                </div>
                <div className="fake-toolbox-icon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="0.604004" y="1" width="14.7925" height="1.32075" rx="0.660377" fill="#272729" />
                    <rect x="0.604004" y="5.22632" width="14.7925" height="1.32075" rx="0.660377" fill="#272729" />
                    <rect x="0.604004" y="9.45288" width="14.7925" height="1.32075" rx="0.660377" fill="#272729" />
                    <rect x="0.604004" y="13.6792" width="14.7925" height="1.32075" rx="0.660377" fill="#272729" />
                  </svg>
                </div>
              </div>
              <div className="icon-divider"></div>
              <div className="flex">
                <div className="fake-toolbox-icon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_2285_3308)">
                      <path d="M4.74512 3.93237V12.068" stroke="#272729" stroke-width="1.255" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M8 5.01685V10.9829" stroke="#272729" stroke-width="1.255" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M11.2549 6.10156V9.89817" stroke="#272729" stroke-width="1.255" stroke-linecap="round" stroke-linejoin="round" />
                      <rect x="0.625" y="0.625" width="14.75" height="14.75" rx="3.375" stroke="#272729" stroke-width="1.25" />
                    </g>
                  </svg>
                </div>
              </div>
              <div className="icon-divider"></div>
              <div className="fake-switch">
                <div className="fake-switch-toggle">
                  <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2.47266" y="8.13818" width="8.94706" height="2.75294" rx="0.688235" stroke="#272729" stroke-width="0.75" />
                    <rect x="0.407959" y="3.32056" width="9.63529" height="2.75294" rx="0.688235" stroke="#272729" stroke-width="0.75" />
                    <path d="M6.08594 1.25598V12.956" stroke="#272729" stroke-width="0.75" stroke-linecap="round" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="section-features">
          <div className="section-feature">
            <div className="section-title-icon">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2.5" y="2.5" width="13" height="13" rx="2.5" stroke="#272729" />
                <rect x="7.5" y="7.5" width="8" height="8" rx="2.5" stroke="#272729" />
              </svg>
              <p>Outline with ease</p>
            </div>
            <div className="section-feature-text">
              <p>Outline your story on the canvas for a free flowing and creative experience. </p>
            </div>
          </div>
          <div className="section-feature">
            <div className="section-title-icon">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.49995 8.78988C8.42816 8.25397 9.57176 8.25396 10.5 8.78986L15.3602 11.5959C16.0269 11.9808 16.0269 12.943 15.3602 13.3279L10.5 16.134C9.57179 16.67 8.42818 16.67 7.49997 16.1341L2.63976 13.328C1.97309 12.9431 1.97309 11.9809 2.63975 11.596L7.49995 8.78988Z" stroke="#272729" />
                <path d="M7.49995 5.32796C8.42816 4.79205 9.57176 4.79204 10.5 5.32795L15.3602 8.13398C16.0269 8.51888 16.0269 9.48112 15.3602 9.86603L10.5 12.6721C9.57179 13.208 8.42818 13.2081 7.49997 12.6721L2.63976 9.86611C1.97309 9.48121 1.97309 8.51897 2.63975 8.13407L7.49995 5.32796Z" fill="white" stroke="#272729" />
                <path d="M7.49995 1.86605C8.42816 1.33014 9.57176 1.33013 10.5 1.86603L15.3602 4.67207C16.0269 5.05697 16.0269 6.01921 15.3602 6.40411L10.5 9.21022C9.57179 9.74613 8.42818 9.74614 7.49997 9.21024L2.63976 6.4042C1.97309 6.0193 1.97309 5.05706 2.63975 4.67215L7.49995 1.86605Z" fill="white" stroke="#272729" />
              </svg>
              <p>Work from multiple views</p>
            </div>
            <div className="section-feature-text">
              <p>Get a top-to-bottom view of your story with the Kanban and List views. </p>
            </div>
          </div>
          <div className="section-feature">
            <div className="section-title-icon">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_1178_7000)">
                  <rect x="4.28351" y="10.4119" width="12.2353" height="3.76471" rx="1" stroke="#040404" strokeWidth="1.25" />
                  <rect x="1.45996" y="3.82349" width="13.1765" height="3.76471" rx="1" stroke="#040404" strokeWidth="1.25" />
                  <path d="M9.22467 1V17" stroke="#040404" strokeWidth="1.25" strokeLinecap="round" />
                </g>
              </svg>
              <p>Visualize your story's rhythm</p>
            </div>
            <div className="section-feature-text">
              <p>Use the beat timeline to visualize your story's rhythm with every turn, reveal, and emotion.</p>
            </div>
          </div>
        </div>
      </section>

      <div className="divider"></div>

      <CharactersLocationsSection />

      <section
        className="command-section"
      >
        <div className="keyboard-keys">
          <div className="key">⌘</div>
          <div className="key">K</div>
        </div>
        <h2 className="section-title">Search and contextual commands, right at your fingertips</h2>
        <p className="section-subtitle">Hit ⌘K to unleash a world of commands, navigation options and search - combined in one glorious experience.</p>
        <div className="fake-command-line-pre-wrapper">
          <div className="fake-command-line">
            <div className="fake-command-line-inner">
              <div className="chip-wrapper">
                <div className="chip">
                  GMARH - Script
                </div>
              </div>
              <div className="flex items-center">
                <div className="caret command-caret"></div>
                <div className="search-field">Type a command or search...</div>
              </div>
              <div className="fake-command-list-wrapper gap-4">
                <div className="command-list-item">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.5 6.33C2.5 4.62153 2.5 3.76729 2.83776 3.11708C3.12239 2.56915 3.56915 2.12239 4.11708 1.83776C4.76729 1.5 5.62153 1.5 7.33 1.5H10.67C12.3785 1.5 13.2327 1.5 13.8829 1.83776C14.4309 2.12239 14.8776 2.56915 15.1622 3.11708C15.5 3.76729 15.5 4.62153 15.5 6.33V11.67C15.5 13.3785 15.5 14.2327 15.1622 14.8829C14.8776 15.4309 14.4309 15.8776 13.8829 16.1622C13.2327 16.5 12.3785 16.5 10.67 16.5H7.33C5.62153 16.5 4.76729 16.5 4.11708 16.1622C3.56915 15.8776 3.12239 15.4309 2.83776 14.8829C2.5 14.2327 2.5 13.3785 2.5 11.67V6.33Z" stroke="#040404" stroke-width="1.25" />
                    <path d="M8.00003 10.5H10" stroke="#040404" stroke-width="1.25" stroke-linecap="round" />
                    <path d="M6.50009 7.5H11.5001" stroke="#040404" stroke-width="2" stroke-linecap="round" />
                  </svg>
                  <div className="command-list-item-title">Switch to Title Page</div>
                </div>
                <div className="command-list-item selected">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.125 16H6C3.79086 16 2 14.2091 2 12V6C2 3.79086 3.79086 2 6 2H12C14.2091 2 16 3.79086 16 6V8.125" stroke="#040404" stroke-width="1.25" stroke-linecap="round" />
                    <circle cx="11.1875" cy="11.1875" r="3.0625" stroke="#040404" stroke-width="1.25" />
                    <path d="M13.375 13.375L15.5625 15.5625" stroke="#040404" stroke-width="1.25" stroke-linecap="round" />
                  </svg>
                  <div className="command-list-item-title">Find and replace</div>
                </div>
                <div className="command-list-item">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.8321 1.93457H6.83C5.12153 1.93457 4.26729 1.93457 3.61708 2.27233C3.06915 2.55696 2.62239 3.00372 2.33776 3.55165C2 4.20186 2 5.0561 2 6.76457V11.1016C2 12.8101 2 13.6644 2.33776 14.3146C2.62239 14.8625 3.06915 15.3093 3.61708 15.5939C4.26729 15.9316 5.12153 15.9316 6.83 15.9316H11.3314C12.8849 15.9316 13.6617 15.9316 14.2642 15.6509C14.904 15.3528 15.4182 14.8386 15.7163 14.1988C15.997 13.5963 15.997 12.8195 15.997 11.266" stroke="#040404" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M14.0134 9.33209L10.3944 8.36239M10.3944 8.36239L11.3641 4.7434M10.3944 8.36239L13.0711 6.81698C14.9402 5.73789 15.5805 3.34798 14.5015 1.47896" stroke="#040404" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  <div className="command-list-item-title">Go to...</div>
                </div>
                <div className="command-list-item">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.125 16H6C3.79086 16 2 14.2091 2 12V6C2 3.79086 3.79086 2 6 2H12C14.2091 2 16 3.79086 16 6V8.125" stroke="#040404" stroke-width="1.25" stroke-linecap="round" />
                    <path d="M13.6443 15.2147C14.078 15.6484 14.7811 15.6484 15.2148 15.2147C15.6485 14.7811 15.6485 14.0779 15.2148 13.6442L14.6804 13.1098L13.1099 14.6803L13.6443 15.2147Z" stroke="#040404" stroke-width="1.25" />
                    <path d="M13.1108 14.6812L14.6804 13.1115L10.3141 8.74521C10.104 8.53509 9.8362 8.39211 9.5447 8.33441L9.13368 8.25306C8.60994 8.1494 8.14867 8.61067 8.25233 9.13441L8.33368 9.54543C8.39138 9.83693 8.53436 10.1047 8.74448 10.3149L13.1108 14.6812Z" stroke="#040404" stroke-width="1.25" />
                    <path d="M9.03555 10.606L10.6052 9.03631" stroke="#040404" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  <div className="command-list-item-title">View script notes</div>
                </div>
                <div className="command-list-item">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1.5" y="5.90674" width="15" height="6.17647" rx="1.5" stroke="#040404" stroke-width="1.25" />
                    <path d="M5.02937 3.75849V14.2316M5.02937 3.75849L3.70584 3.25977M5.02937 3.75849L6.3529 3.25977M5.02937 14.2316L3.70584 14.7304M5.02937 14.2316L6.3529 14.7304" stroke="#040404" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  <div className="command-list-item-title">Show timeline</div>
                </div>
                <div className="command-list-item">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_1070_23350)">
                      <rect x="4.28348" y="10.4116" width="12.2353" height="3.76471" rx="1" stroke="#040404" stroke-width="1.25" />
                      <rect x="1.45996" y="3.82373" width="13.1765" height="3.76471" rx="1" stroke="#040404" stroke-width="1.25" />
                      <path d="M9.22467 1V17" stroke="#040404" stroke-width="1.25" stroke-linecap="round" />
                    </g>
                  </svg>
                  <div className="command-list-item-title">Show story map</div>
                </div>
                <div className="command-list-item">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_1071_23392)">
                      <path d="M5.87501 7.4375H5.27084C4.16627 7.4375 3.27084 8.33293 3.27084 9.4375V14.8125C3.27084 15.9171 4.16627 16.8125 5.27084 16.8125H12.7292C13.8337 16.8125 14.7292 15.9171 14.7292 14.8125V9.4375C14.7292 8.33293 13.8337 7.4375 12.7292 7.4375H12.125" stroke="#040404" stroke-width="1.25" stroke-linecap="round" />
                      <path d="M9.00003 10.5625V1.1875M9.00003 1.1875L5.87503 4.3125M9.00003 1.1875L12.125 4.3125" stroke="#040404" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                    </g>
                  </svg>
                  <div className="command-list-item-title">Export PDF</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ThinkDreamSection />

      <div className="divider"></div>

      <CTASection />

    </div>
  );
};

export default Home;


